import { UzTabs } from '@utazzo/ui';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { NotificationAudiencesTab } from '../components/tabs/NotificationAudiencesTab';
import { NotificationChannelsTab } from '../components/tabs/NotificationChannelsTab';
import { NotificationEventsTab } from '../components/tabs/NotificationEventsTab';
import { NotificationHistoryTab } from '../components/tabs/NotificationHistoryTab';
import { NotificationRulesTab } from '../components/tabs/NotificationRulesTab';
import { NotificationTemplatesTab } from '../components/tabs/NotificationTemplatesTab';
import { WhatsAppTemplatesTab } from '../components/tabs/WhatsAppTemplatesTab';

import { PageShell } from '@/components/page-shell';
import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

/**
 * Notification engine admin (ADR-0048). Six concept-area tabs match the
 * server-side surface 1:1 - Events / Channels / Templates / Audiences /
 * Rules / History - rendered under a single `/notifications` route so
 * the operator's mental model stays "notifications is one thing, with
 * panels for each piece".
 *
 * Tab state is URL-driven (`?tab=events|channels|...`) so a shared link
 * lands the recipient on the same panel.
 */

const TAB_VALUES = [
  'events',
  'channels',
  'templates',
  'whatsapp',
  'audiences',
  'rules',
  'history',
] as const;
type Tab = (typeof TAB_VALUES)[number];

const isTab = (v: string | null): v is Tab =>
  v !== null && (TAB_VALUES as ReadonlyArray<string>).includes(v);

export const NotificationsPage = (): React.JSX.Element => {
  const [params] = useSearchParams();
  const writeParams = useUrlParamWriter();
  const raw = params.get('tab');
  const tab: Tab = isTab(raw) ? raw : 'events';

  // Merges into the LIVE URL so the History tab's grid state (rows-per-page,
  // filters, sort - all written by nuqs, invisible to React Router's params
  // snapshot) survives a tab switch.
  const setTab = (next: Tab): void => {
    writeParams((p) => p.set('tab', next));
  };

  return (
    <PageShell
      title="Notifications"
      description="Event catalog, channels, templates, audiences, and delivery rules. Admin-only."
    >
      <UzTabs
        value={tab}
        onValueChange={(v) => setTab(v as Tab)}
        // Phones: the seven tabs don't fit, so the strip scrolls sideways.
        listClassName="max-sm:max-w-full max-sm:justify-start max-sm:overflow-x-auto"
        tabs={[
          { value: 'events', label: 'Events', content: <NotificationEventsTab /> },
          { value: 'channels', label: 'Channels', content: <NotificationChannelsTab /> },
          { value: 'templates', label: 'Templates', content: <NotificationTemplatesTab /> },
          { value: 'whatsapp', label: 'WhatsApp', content: <WhatsAppTemplatesTab /> },
          { value: 'audiences', label: 'Audiences', content: <NotificationAudiencesTab /> },
          { value: 'rules', label: 'Rules', content: <NotificationRulesTab /> },
          { value: 'history', label: 'History', content: <NotificationHistoryTab /> },
        ]}
      />
    </PageShell>
  );
};
